import { Button } from "@/components/ui/button";
import {
  createInspectorSearchParams,
  createPromptSearchParams,
} from "@/lib/inspector/navigation";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { TraceDetailsSection } from "../TraceDetails";
import { TraceEvents } from "../TraceEvents";
import type { EnhancedSpan, EnhancedTrace } from "../types";

interface InspectorTestableResult {
  type: "tool" | "prompt" | "resource" | null;
  name: string | null;
  params: Record<string, string> | null;
}

function extractParamsFromSpan(
  span: EnhancedSpan,
): Record<string, string> | null {
  try {
    if (span.attributes && typeof span.attributes === "object") {
      const params: Record<string, string> = {};

      // Extract MCP request arguments from attributes
      for (const [key, value] of Object.entries(span.attributes)) {
        if (key.startsWith("mcp.request.argument.")) {
          const paramName = key.replace("mcp.request.argument.", "");
          if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            params[paramName] = String(value);
          }
        }
      }

      return Object.keys(params).length > 0 ? params : null;
    }
  } catch {
    // Ignore parsing errors
  }

  return null;
}

function isInspectorTestable(trace: EnhancedTrace): InspectorTestableResult {
  const span = trace.spans[0] as EnhancedSpan;
  if (!span) {
    return { type: null, name: null, params: null };
  }

  // Tool detection
  if (span.mcpToolName) {
    return {
      type: "tool",
      name: span.mcpToolName,
      params: extractParamsFromSpan(span),
    };
  }

  // Prompt detection
  if (span.mcpMethodName?.startsWith("prompts/")) {
    return {
      type: "prompt",
      name: span.mcpMethodName.replace("prompts/", ""),
      params: extractParamsFromSpan(span),
    };
  }

  // Resource detection
  if (span.mcpMethodName?.startsWith("resources/")) {
    return {
      type: "resource",
      name: span.mcpMethodName.replace("resources/", ""),
      params: null, // Resources don't have parameters
    };
  }

  return { type: null, name: null, params: null };
}

interface TraceExpandedContentProps {
  trace: EnhancedTrace;
  showInspectorNavigation?: boolean;
  projectId?: string;
}

export function TraceExpandedContent({
  trace,
  showInspectorNavigation = false,
  projectId,
}: TraceExpandedContentProps) {
  const hasSpans = trace.spans && trace.spans.length > 0;

  if (hasSpans) {
    console.warn(
      "Multi-span trace detected. This is not really supported yet.",
      trace,
    );
  }

  // Determine if this trace can be tested in the inspector
  const inspectorTestable =
    showInspectorNavigation && projectId
      ? isInspectorTestable(trace)
      : { type: null, name: null, params: null };
  const canNavigateToInspector =
    inspectorTestable.type && inspectorTestable.name;

  // Generate navigation search params
  const getNavigationParams = () => {
    if (!canNavigateToInspector || !inspectorTestable.name) {
      return null;
    }

    if (inspectorTestable.type === "tool") {
      return createInspectorSearchParams(
        inspectorTestable.name,
        inspectorTestable.params || undefined,
      );
    }

    if (inspectorTestable.type === "prompt") {
      return createPromptSearchParams(
        inspectorTestable.name,
        inspectorTestable.params || undefined,
      );
    }

    if (inspectorTestable.type === "resource") {
      // Resources use inspector tab with inspectorTab=resources
      return { tab: "inspector" as const, inspectorTab: "resources" as const };
    }

    return null;
  };

  const navigationParams = getNavigationParams();

  return (
    <div className="@container mb-2">
      {/* Navigation button (if enabled and testable) */}
      {canNavigateToInspector && navigationParams && projectId && (
        <div className="mb-4 flex justify-end">
          <Button variant="outline" size="sm" asChild>
            <Link
              to="/projects/$projectId"
              params={{ projectId }}
              search={navigationParams}
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Try in Inspector
            </Link>
          </Button>
        </div>
      )}

      <div
        className={cn("grid gap-4 w-full", {
          "grid-cols-1": !hasSpans,
          "grid-cols-1 @[800px]:grid-cols-2": hasSpans,
        })}
      >
        {/* Transport-specific details section */}
        <TraceDetailsSection trace={trace} />

        {/* Events/Logs section for single span traces */}
        {hasSpans && <TraceEvents span={trace.spans[0]} />}
      </div>
    </div>
  );
}
